<template>
  <v-dialog
    v-model="dialogProps.visibilidade"
    max-width="680px"
    persistent
  >
    <v-card class="pa-5">
      <v-card-title>
        <v-row class="d-flex flex-column text-lg-center">
          <h4 class="headline primary--text text-no-wrap">
            {{ labelBtnAcao }} Escolaridade
          </h4>
          <span class="subtitle-1">Informe seus dados de escolaridade</span>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-form
          ref="form"
          v-model="dialogProps.valid"
        >
          <!-- nível de escolaridade -->
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="escolaridade.nivelEscolaridade"
                :items="niveisEscolaridade"
                :rules="[(v) => !!v || 'seleção obrigatória']"
                item-text="descricao"
                label="Nível de Escolaridade"
                prepend-icon="menu_book"
                required
                return-object
              />
            </v-col>
          </v-row>

          <v-row v-if="deveInformarNomeCurso || deveEscolherCurso">
            <v-col
              v-if="deveEscolherCurso"
              cols="12"
            >
              <v-autocomplete
                v-model="escolaridade.curso"
                :items="cursos"
                :rules="[(v) => !!v || 'Selecione um curso']"
                :search-input.sync="cursoSearch"
                item-text="nome"
                label="Curso"
                placeholder="Digite para pesquisar..."
                persistent-hint
                prepend-icon="school"
                required
                return-object
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        O curso
                        <strong class="secondary--text">{{
                          cursoSearch
                        }}</strong>
                        não foi encontrado.
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- se o curso selecionado for o Outro -> abrir text field para informar o nome do
            curso a ser cadastrado -->
            <v-col
              v-if="deveInformarNomeCurso"
              cols="12"
            >
              <v-text-field
                v-model.trim="escolaridade.nomeCursoOutro"
                :rules="ruleNotEmpty"
                label="Nome do Curso"
                prepend-icon="school"
                required
              />
            </v-col>
          </v-row>

          <!-- em caso de nível = Residência Médica -> informar o tipo de residência -->
          <v-row>
            <v-col
              v-if="deveInformarResidenciaEm"
              cols="12"
            >
              <v-text-field
                v-model.trim="escolaridade.residenciaEm"
                :rules="ruleNotEmpty"
                label="Tipo de Residência Médica"
                prepend-icon="school"
                required
              />
            </v-col>
          </v-row>

          <!-- instituição de ensino -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model.trim="escolaridade.instituicao"
                :rules="ruleNotEmpty"
                label="Instituição"
                prepend-icon="account_balance"
                required
              />
            </v-col>
          </v-row>

          <!-- situação do curso -->
          <v-row>
            <v-col cols="12">
              <h4>Situação do Curso</h4>
              <v-radio-group
                v-model="escolaridade.status.id"
                :rules="[(v) => !!v || 'Selecione o status do curso']"
                required
                row
              >
                <v-radio
                  v-for="status in statusEscolaridade"
                  :key="status.id"
                  :label="status.descricao"
                  :value="status.id"
                />
              </v-radio-group>
            </v-col>
          </v-row>

          <!-- início e término -->
          <v-row>
            <v-col cols="12">
              <h4>Período</h4>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <app-date-select
                    tipo="month"
                    label="Início"
                    tooltip="Data de início da escolaridade"
                    :data-inicial="escolaridade.inicio"
                    :max="maxInicioEscolaridade"
                    @return="(value) => (escolaridade.inicio = value)"
                  />
                </v-col>
                <v-col
                  v-if="!statusAndamentoEscolaridade"
                  cols="12"
                  md="6"
                >
                  <app-date-select
                    tipo="month"
                    :label="labelCampoTerminoEscolaridade"
                    tooltip="Data de término da escolaridade"
                    :data-inicial="escolaridade.termino"
                    :min="minTerminoEscolaridade"
                    :max="maxTerminoEscolaridade"
                    :disabled="escolaridade.inicio === ''"
                    @return="(value) => (escolaridade.termino = value)"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-row class="justify-end mr-4">
          <!-- botão de ação -->
          <v-btn
            :disabled="!dialogProps.valid"
            class="mr-2"
            color="primary"
            @click="doAcao"
          >
            {{ labelBtnAcao }}
          </v-btn>

          <!-- botão de cancelar -->
          <v-btn
            color="primary"
            outlined
            @click="dialogProps.visibilidade = false"
          >
            Cancelar
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import AppDateSelect from '@/components/core/AppDateSelect';
import Data from '@/modules/data';

const escolaridadeRepository = RepositoryFactory.get('escolaridade');

const DEFAULT_ESCOLARIDADE = () => ({
  id: null,
  nivelEscolaridade: null,
  curso: null,
  residenciaEm: null,
  instituicao: null,
  status: { id: null },
  inicio: null,
  termino: null,
  nomeCursoOutro: null,
});

const STATUS_ESCOLARIDADE_MAX_TERMINO_HOJE = new Set(['CONCLUIDO', 'INCOMPLETO']);

const NIVEL_ESCOLARIDADE_EXIGE_CURSO_DA_LISTA = new Set(['GRADUACAO']);

const NIVEL_ESCOLARIDADE_EXIGE_NOME_CURSO = new Set([
  'ENSINO_PROFISSIONAL_TECNICO',
  'ESPECIALIZACAO',
  'MESTRADO_ACADEMICO',
  'MESTRADO_PROFISSIONAL',
  'DOUTORADO_ACADEMICO',
  'DOUTORADO_PROFISSIONAL',
  'POS_DOUTORADO',
]);

export default {
  name: 'DialogFormacaoAcademica',

  components: { AppDateSelect },

  props: {
    dialogProps: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      cursoSearch: '',
      escolaridade: DEFAULT_ESCOLARIDADE(),
      dataAtual: Data.get('YYYY-MM-DD'),
      ruleNotEmpty: [(v) => (v && !!v.trim()) || 'Campo Obrigatório'],
      niveisEscolaridade: [
        { id: 'ENSINO_FUNDAMENTAL', descricao: 'Ensino Fundamental' },
        { id: 'ENSINO_MEDIO', descricao: 'Ensino Médio' },
        { id: 'ENSINO_PROFISSIONAL_TECNICO', descricao: 'Ensino Profissional de Nível Técnico' },
        { id: 'GRADUACAO', descricao: 'Graduação' },
        { id: 'ESPECIALIZACAO', descricao: 'Especialização' },
        { id: 'ESPECIALIZACAO_RESIDENCIA_MEDICA', descricao: 'Especialização - Residência médica' },
        { id: 'MESTRADO_ACADEMICO', descricao: 'Mestrado Acadêmico' },
        { id: 'MESTRADO_PROFISSIONAL', descricao: 'Mestrado Profissional' },
        { id: 'DOUTORADO_ACADEMICO', descricao: 'Doutorado Acadêmico' },
        { id: 'DOUTORADO_PROFISSIONAL', descricao: 'Doutorado Profissional' },
        { id: 'POS_DOUTORADO', descricao: 'Pós-Doutorado' },
      ],
      statusEscolaridade: [
        { id: 'INCOMPLETO', descricao: 'Incompleto' },
        { id: 'EM_ANDAMENTO', descricao: 'Em andamento' },
        { id: 'CONCLUIDO', descricao: 'Concluído' },
      ],
    };
  },

  computed: {
    ...mapState({
      cursos: (state) => state.perfil.cursos,
      usuario: (state) => state.auth.usuario,
    }),
    escolaridades: {
      get() {
        return this.$store.state.perfil.escolaridades;
      },
      set(escolaridades) {
        this.$store.dispatch('perfil/setEscolaridades', escolaridades);
      },
    },
    labelBtnAcao() {
      if (this.dialogProps.acao === 'editar') {
        return 'Editar';
      }
      if (this.dialogProps.acao === 'cadastrar') {
        return 'Adicionar';
      }
      return null;
    },
    minTerminoEscolaridade() {
      return this.escolaridade.inicio;
    },
    maxTerminoEscolaridade() {
      if (this.escolaridade.status && STATUS_ESCOLARIDADE_MAX_TERMINO_HOJE.has(this.escolaridade.status.id)) {
        return this.dataAtual;
      }
      return '';
    },
    deveInformarResidenciaEm() {
      return (
        this.escolaridade.nivelEscolaridade
        && this.escolaridade.nivelEscolaridade.id === 'ESPECIALIZACAO_RESIDENCIA_MEDICA'
      );
    },
    deveInformarNomeCurso() {
      return (
        (this.escolaridade.nivelEscolaridade
          && NIVEL_ESCOLARIDADE_EXIGE_NOME_CURSO.has(this.escolaridade.nivelEscolaridade.id))
        || (this.deveEscolherCurso && this.escolaridade.curso && this.escolaridade.curso.isOutro)
      );
    },
    deveEscolherCurso() {
      return (
        this.escolaridade.nivelEscolaridade
        && NIVEL_ESCOLARIDADE_EXIGE_CURSO_DA_LISTA.has(this.escolaridade.nivelEscolaridade.id)
      );
    },
    maxInicioEscolaridade() {
      return this.dataAtual;
    },
    labelCampoTerminoEscolaridade() {
      return this.escolaridade.status && this.escolaridade.status.id === 'INCOMPLETO' ? 'Interrupção' : 'Término';
    },
    statusAndamentoEscolaridade() {
      return this.escolaridade.status && this.escolaridade.status.id === 'EM_ANDAMENTO';
    },
    msgSucessoAcao() {
      if (this.dialogProps.acao === 'cadastrar') {
        return 'Escolaridade cadastrada com sucesso!';
      }
      if (this.dialogProps.acao === 'editar') {
        return 'Escolaridade editada com sucesso!';
      }
      return null;
    },
  },

  watch: {
    'dialogProps.visibilidade': function (visibilidade) {
      if (visibilidade) {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        if (this.dialogProps.acao === 'editar') {
          // deep copy
          this.escolaridade = JSON.parse(JSON.stringify(this.dialogProps.escolaridade));
        }
        if (this.dialogProps.acao === 'cadastrar') {
          this.escolaridade = DEFAULT_ESCOLARIDADE();
        }
      } else {
        this.escolaridade = DEFAULT_ESCOLARIDADE();
      }
    },
  },

  methods: {
    doAcao() {
      if (!this.deveEscolherCurso) {
        this.escolaridade.curso = null;
      }
      if (!this.deveInformarNomeCurso) {
        this.escolaridade.nomeCursoOutro = null;
      }
      if (this.deveEscolherCurso && !this.escolaridade.curso.isOutro) {
        this.escolaridade.nomeCursoOutro = null;
      }
      if (!this.deveInformarResidenciaEm) {
        this.escolaridade.residenciaEm = null;
      }

      this.escolaridade.inicio = Data.create(this.escolaridade.inicio).startOf('month').format('YYYY-MM-DD');
      if (this.statusAndamentoEscolaridade) {
        this.escolaridade.termino = null;
      } else {
        this.escolaridade.termino = Data.create(this.escolaridade.termino).endOf('month').format('YYYY-MM-DD');
      }

      if (this.dialogProps.acao === 'editar') {
        escolaridadeRepository.update(this.escolaridade).then((res) => {
          const escolaridade = res.data;
          const idx = this.usuario.perfil.escolaridades.findIndex((e) => e.id === escolaridade.id);
          this.usuario.perfil.escolaridades.splice(idx, 1, escolaridade);
          this.dialogProps.visibilidade = false;
          this.$store.dispatch('app/success', {
            response: res,
            message: this.msgSucessoAcao,
          });
        });
      } else if (this.dialogProps.acao === 'cadastrar') {
        escolaridadeRepository.create(this.escolaridade).then((res) => {
          this.usuario.perfil.escolaridades.push(res.data);
          this.usuario.perfil.escolaridades.sort((a, b) => a.inicio.localeCompare(b.inicio));
          this.dialogProps.visibilidade = false;
          this.$store.dispatch('app/success', {
            response: res,
            message: this.msgSucessoAcao,
          });
        });
      }
    },
  },
};
</script>
